import { useQuery } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { getServiceDependency, getGovernanceMessage } from '@/lib/serviceDependencyRegistry';

export interface SeekServiceGovernanceState {
  allowed: boolean;
  isLoading: boolean;
  isGlobalOff: boolean;
  isBackendBlocked: boolean;
  message: string | null;
  isReadOnly: boolean;
  shouldHide: boolean;
  shouldDisable: boolean;
}

/**
 * Governance hook for ForkSeek portal.
 * Checks global services.is_active and returns governance-aware state
 * with UI-cascading helpers (shouldHide, shouldDisable, isReadOnly).
 */
export function useSeekServiceGovernance(serviceKey: string): SeekServiceGovernanceState {
  const { data, isLoading } = useQuery({
    queryKey: ['seek-service-governance', serviceKey],
    queryFn: async () => {
      const { data: svc, error } = await seekSupabase
        .from('services')
        .select('is_active')
        .eq('service_key', serviceKey)
        .maybeSingle();
      if (error || !svc) return { allowed: false, isGlobalOff: false, isBackendBlocked: true };
      return { allowed: svc.is_active, isGlobalOff: !svc.is_active, isBackendBlocked: false };
    },
    staleTime: 5 * 60 * 1000,
  });

  const allowed = data?.allowed ?? false;
  const isGlobalOff = data?.isGlobalOff ?? false;
  const isBackendBlocked = data?.isBackendBlocked ?? false;
  const blockReason = isGlobalOff ? 'service_globally_disabled' : null;

  return {
    allowed,
    isLoading,
    isGlobalOff,
    isBackendBlocked,
    message: isBackendBlocked
      ? 'Subscription access could not be verified. Please try again or contact support.'
      : getGovernanceMessage(serviceKey, blockReason),
    isReadOnly: isGlobalOff,
    shouldHide: isGlobalOff,
    shouldDisable: !allowed,
  };
}
